
import React, { useState, useEffect } from 'react';
import { Share2, X, Loader2, Trash2, Users } from 'lucide-react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (email: string) => Promise<void>;
  onRevoke?: (email: string) => Promise<void>;
  currentSharedWith?: string[];
  title?: string;
  description?: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  onRevoke,
  currentSharedWith = [],
  title = "Share Access", 
  description = "Allow another user to view this item. Enter their email below." 
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);

  // Reset email when modal opens
  useEffect(() => {
    if (isOpen) {
        setEmail('');
        setIsLoading(false);
        setRevokingEmail(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      await onConfirm(email);
      setEmail(''); // Clear on success so they can add another
    } catch (e) {
      console.error(e);
      // Parent handles error toast
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (emailToRevoke: string) => {
      if (!onRevoke) return;
      if (!confirm(`Remove access for ${emailToRevoke}?`)) return;
      
      setRevokingEmail(emailToRevoke);
      try {
          await onRevoke(emailToRevoke);
      } catch (e) {
          console.error(e);
      } finally {
          setRevokingEmail(null);
      }
  };

  return (
    <div 
        className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" 
        onClick={onClose}
    >
        <div 
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl" 
            onClick={e => e.stopPropagation()}
        >
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Share2 size={18} /> {title}
                </h3>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                    <X size={20} />
                </button>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">{description}</p>
            
            <div className="space-y-4 mb-6">
                <div className="flex gap-2">
                    <input 
                        type="email" 
                        placeholder="user@example.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow text-sm"
                        autoFocus
                    />
                    <button 
                        onClick={handleSubmit} 
                        disabled={isLoading || !email}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors text-sm"
                    >
                        {isLoading ? <Loader2 className="animate-spin" size={18} /> : 'Add'}
                    </button>
                </div>
            </div>

            {currentSharedWith.length > 0 && (
                <div className="border-t border-slate-100 pt-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
                        <Users size={12} /> Currently Shared With ({currentSharedWith.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                        {currentSharedWith.map((sharedEmail) => (
                            <div key={sharedEmail} className="flex justify-between items-center bg-slate-50 p-2 rounded-lg group">
                                <span className="text-sm text-slate-700 truncate mr-2">{sharedEmail}</span>
                                {onRevoke && (
                                    <button 
                                        onClick={() => handleRevoke(sharedEmail)}
                                        disabled={!!revokingEmail}
                                        className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
                                        title="Remove Access"
                                    >
                                        {revokingEmail === sharedEmail ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    </div>
  );
};
